import React, { useEffect, useRef, useState, useTransition } from "react";
import useReadHistoryEntryService from "../../hooks/useReadHistoryEntryService";
import { readHistoryEntryService } from "../../services/readHistoryEntryService";
import { useAuth } from "../../context/AuthContext";
import { BlobLoader } from "../../components/loader";

function extractEncodedIdOnly(fullId) {
  const match = fullId.match(/(_x28_.*?_x29_)/);
  return match ? match[1] : null;
}

function extractOriginalPath(encoded) {
  if (!encoded) return null;
  const decoded = encoded.replace(/_x([0-9A-Fa-f]{2})_/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  const match = decoded.match(/\((.*?)\)/);
  if (!match) return null;
  return match[1]
    .replace(/_/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function decodeIdToOriginalPath(input) {
  if (!input) return "";
  const decoded = input.replace(/_x([0-9A-Fa-f]{2})_/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  const match = decoded.match(/\((.*?)\)/);
  return match ? match[1] : decoded;
}

let currentAudio;
function playAudio(path) {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
  }
  currentAudio = new Audio(path);
  currentAudio.play().catch((err) => console.error("Audio error:", err));
}
function StopAudio(path) {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
  }
}

function getCurrentGoogleTranslateLanguage() {
  const select = document.querySelector(".goog-te-combo");
  return select ? select.value : null;
}

const LayeredSVG = ({ src, category, subtopic_data }) => {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isPending, startTransition] = useTransition();
  const adioURL = "https://file.fefdygo.com/";
  const [firstHighlight, setFirstHighlight] = useState(false);
  const [returnAudioIds, setReturnAudioIds] = useState([]);
  const [compAudioIds, setCompAudioIds] = useState([]);

  const [audioIds, setAudioIds] = useState([]);
  const audioIdsRef = useRef([]);
  const compIds = useRef([]);
  const returnAudioIdsRef = useRef([]);
  const { auth, setCommonWarning, setCommonSuccess, breadcrumbs } = useAuth();
  const [checkFirstHit, setCheckFirstHit] = useState(false);

  // Preload fetch once per src
  const MIN_LOADER_TIME = 500; 
  // Preload fetch once per src
  useEffect(() => {
    let controller = new AbortController();
    const startTime = Date.now();
    setIsLoading(true);
    setError("");

    fetch(src, { signal: controller.signal })
      .then((res) => res.text())
      .then((data) => {
        const cleanedXml = data.replace(/<i:[^>]+>[\s\S]*?<\/i:[^>]+>/g, "");
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(MIN_LOADER_TIME - elapsed, 0);

        setTimeout(() => {
          startTransition(() => {
            setSvgContent(cleanedXml);
            setIsLoading(false);
          });
        }, remaining);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Error loading file:", err);
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(MIN_LOADER_TIME - elapsed, 0);
          setTimeout(() => {
            setError("Sorry, the file could not be loaded.");
            setIsLoading(false);
          }, remaining);
        }
      });

    return () => controller.abort();
  }, [src]);

  // Parse and inject SVG, add events
  useEffect(() => {
    if (!svgContent || !containerRef.current) return;

    requestAnimationFrame(() => {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
      const svgElement = svgDoc.querySelector("svg");

      if (!svgElement) {
        setError("Sorry, the file could not be loaded.");
        return;
      }

      const svgContainer = containerRef.current;
      svgContainer.innerHTML = "";
      svgContainer.appendChild(svgElement);

      const highlightColor = "#689E41";
      const textHighlight = "#e80707";

      const resetHighlights = () => {
        svgElement.querySelectorAll(".highlight").forEach((el) => {
          el.classList.remove("highlight", "boom-pulse");
          el.style.filter = "";
        });
        svgElement.querySelectorAll("line").forEach((line) => {
          line.classList.remove("boom-pulse");
          line.style.filter = "";
          line.style.stroke = "";
        });
        svgElement.querySelectorAll("text").forEach((text) => {
          text.classList.remove("boom-pulse-text");
          text.style.fill = "";
        });
      };

      const updateNCheckCompAudioIds = async (audioIds, compAudioIds, type) => {
        if (!audioIds || audioIds.length === 0) return;

        // Block multiple hits for "check"
        if (type === "check" && checkFirstHit) {
          console.log("multiple check hits are not allowed");
          return;
        }

        // Set the flag BEFORE the API call
        if (type === "check") setCheckFirstHit(true);

        try {
          const subtopic =
            breadcrumbs.find((item) => item.path === "/categories")?.id || null;
          const data = await readHistoryEntryService(
            auth.token,
            audioIds,
            compAudioIds,
            type,
            subtopic,
            category,
            subtopic_data
          );

          if (data.status === 200 || data.status === 400) {
            setReturnAudioIds(data.ret_audio_ids);
            returnAudioIdsRef.current = data.ret_audio_ids;
          }

          // if (data.status === 400) setCommonWarning(data.message);
        } catch (error) {
          console.error("Error fetching subjects:", error);
        }
      };

      const handleEnter = async (allIds, id, isText, isAll) => {
        const encodedId = extractEncodedIdOnly(id);
        const path = extractOriginalPath(id);
        if (!path) return;
        const cleanId = decodeIdToOriginalPath(path);

        // If this is a manual hover (not isAll), reset red highlights for this specific element
        if (!isAll) {
          resetHighlights(); // Reset all highlights first
        }

        if (isText) {
          const target =
            svgElement.querySelector(`path#${CSS.escape(cleanId)}`) ||
            svgElement.querySelector(`g#${CSS.escape(cleanId)}`);
          if (target) {
            target.classList.add("highlight");
            target.classList.remove("boom-pulse"); // Remove red pulse animation
            target.style.filter = `
drop-shadow(0 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(0 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(0 -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
`;
          }
          const line = svgElement.querySelector(
            `line[id^="${CSS.escape(encodedId)}"]`
          );
          if (line) {
            line.classList.remove("boom-pulse"); // Remove red pulse animation
            line.style.filter = `
drop-shadow(0 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(1px 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(-1px 0 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(0 1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
drop-shadow(0 -1px 0 ${isAll ? 'rgb(220, 80, 80)' : highlightColor})
`;
            line.style.stroke = isAll ? "rgb(220, 80, 80)" : highlightColor;
          }
          if (!isAll && !compIds.current.includes(cleanId)) {
            compIds.current.push(decodeIdToOriginalPath(id));
          }
          if (!isAll) playAudio(adioURL + cleanId);
        } else if (isAll) {
          audioIdsRef.current = allIds;
          await updateNCheckCompAudioIds(audioIdsRef.current, [], "check");
          const latestReturnAudioIds = returnAudioIdsRef.current;
          const normalize = (str) => str.trim().replace(/^\/+|\/+$/g, "");

          const cleanIdPath = decodeIdToOriginalPath(id);

          if (
            !latestReturnAudioIds.some(
              (id) => normalize(id) === normalize(cleanIdPath)
            )
          ) {
            return false;
          }

          if (isText) {
            const target =
              svgElement.querySelector(`path#${CSS.escape(cleanId)}`) ||
              svgElement.querySelector(`g#${CSS.escape(cleanId)}`);
            if (target) {
              target.classList.add("highlight");
              target.style.filter = `drop-shadow(0 0 6px rgb(220, 80, 80))`;
            }

            const line = svgElement.querySelector(
              `line[id^="${CSS.escape(encodedId)}"]`
            );
            if (line) {
              line.style.filter = `drop-shadow(0 0 6px rgb(220, 80, 80))`;
              line.style.stroke = "rgb(220, 80, 80)";
            }
          } else {
            const group = svgElement.querySelector(`g#${CSS.escape(id)}`);
            if (!group) return;

            // Add infinite pulse animation
            group.classList.add("highlight", "boom-pulse");

            const shadowFilter = `drop-shadow(0 0 0 rgb(255, 80, 80)) drop-shadow(1px 1px 0 rgb(255, 80, 80)) drop-shadow(-1px -1px 0 rgb(255, 80, 80)) drop-shadow(1px -1px 0 rgb(255, 80, 80)) drop-shadow(-1px 1px 0 rgb(255, 80, 80))`;
            group.style.filter = shadowFilter;

            const line = svgElement.querySelector(`line#${CSS.escape(id)}`);
            if (line) {
              line.classList.add("boom-pulse");
              line.style.filter = shadowFilter;
              line.style.stroke = "#f3ffc4";
            }

            const text = svgElement.querySelector(`text#${CSS.escape(id)}`);
            if (text) {
              text.classList.add("boom-pulse-text");
              text.style.fill = "rgb(220, 80, 80)";
            }
          }
          // Removed the setTimeout - red highlights stay until hovered
        } else {
          const group = svgElement.querySelector(`g#${CSS.escape(id)}`);
          if (!group) return;
          group.classList.add("highlight");
          group.classList.remove("boom-pulse", "boom-pulse-text"); // Remove red animations
          group.style.filter = `drop-shadow(0 0 0 ${highlightColor})
drop-shadow(1px 1px 0 ${highlightColor})
drop-shadow(-1px -1px 0 ${highlightColor})
drop-shadow(1px -1px 0 ${highlightColor})
drop-shadow(-1px 1px 0 ${highlightColor})
drop-shadow(1px 0 0 ${highlightColor})
drop-shadow(-1px 0 0 ${highlightColor})
drop-shadow(0 1px 0 ${highlightColor})
drop-shadow(0 -1px 0 ${highlightColor})`;

          const line = svgElement.querySelector(`line#${CSS.escape(id)}`);
          if (line) {
            line.classList.remove("boom-pulse"); // Remove red animation
            line.style.filter = `drop-shadow(0 0 0 ${highlightColor})
drop-shadow(1px 1px 0 ${highlightColor})
drop-shadow(-1px -1px 0 ${highlightColor})
drop-shadow(1px -1px 0 ${highlightColor})
drop-shadow(-1px 1px 0 ${highlightColor})
drop-shadow(1px 0 0 ${highlightColor})
drop-shadow(-1px 0 0 ${highlightColor})
drop-shadow(0 1px 0 ${highlightColor})
drop-shadow(0 -1px 0 ${highlightColor})`;
            line.style.stroke = "#f3ffc4";
          }

          const text = svgElement.querySelector(`text#${CSS.escape(id)}`);
          if (text) {
            text.classList.remove("boom-pulse-text"); // Remove red animation
            text.style.fill = textHighlight;
          }

          const lang = getCurrentGoogleTranslateLanguage();
          const parts = cleanId.split("/");
          const insertIndex = parts.indexOf("techno") + 1;
          let ext_path = null;
          switch (lang) {
            case "ta":
              ext_path = "tamil";
              break;
            case "ml":
              ext_path = "malayalam";
              break;
            case "hi":
              ext_path = "hindi";
              break;
            case "te":
              ext_path = "telugu";
              break;
            default:
              break;
          }
          if (insertIndex > 0 && ext_path) {
            parts.splice(insertIndex, 0, ext_path);
          }
          if (!compIds.current.includes(cleanId)) {
            compIds.current.push(decodeIdToOriginalPath(id));
          }
          playAudio(adioURL + parts.join("/"));
        }

        if (compIds.current.length > 0 && !isAll) {
          updateNCheckCompAudioIds(
            audioIdsRef.current,
            compIds.current,
            "update"
          );
        }
      };

      const handleLeave = () => {
        resetHighlights();
        window.speechSynthesis.cancel();
      };

      // Add event listeners
      const groups = svgElement.querySelectorAll("g[id]");

      const ids = [];
      groups.forEach((group) => {
        if (!ids.includes(decodeIdToOriginalPath(group.id)))
          ids.push(decodeIdToOriginalPath(group.id));

        group.style.cursor = "pointer";
        group.addEventListener("mouseenter", () =>
          handleEnter([], group.id, false, false)
        );
        group.addEventListener("mouseleave", handleLeave);
      });

      const texts = svgElement.querySelectorAll("text[id]");

      texts.forEach((text) => {
        if (!ids.includes(decodeIdToOriginalPath(text.id)))
          ids.push(decodeIdToOriginalPath(text.id));

        text.style.cursor = "pointer";
        text.addEventListener("mouseenter", () =>
          handleEnter([], text.id, true, false)
        );
        text.addEventListener("mouseleave", handleLeave);
      });
      setAudioIds(ids);
      // console.log("ids", ids);
      if (!firstHighlight) {
        groups.forEach((group) => {
          group.style.cursor = "pointer";
          handleEnter(ids, group.id, false, true);
        });
        texts.forEach((text) => {
          handleEnter(ids, text.id, true, true);
        });
        setFirstHighlight(true);
      }
    });
  }, [svgContent]);

  const handleFullscreen = () => {
    const elem = containerRef.current;
    if (!document.fullscreenElement) {
      elem.requestFullscreen?.();
      elem.webkitRequestFullscreen?.();
      elem.msRequestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const showPopup = (targetElement, message) => {
    // Remove any existing popup
    const existing = document.querySelector(".svg-popup");
    if (existing) existing.remove();

    // Get bounding box for position
    const rect = targetElement.getBoundingClientRect();

    // Create popup element
    const popup = document.createElement("div");
    popup.className = "svg-popup";
    popup.textContent = message;

    // Style the popup (simple, clean)
    Object.assign(popup.style, {
      position: "fixed",
      top: `${rect.top - 30}px`,
      left: `${rect.left + rect.width / 2}px`,
      transform: "translateX(-50%)",
      background: "rgba(255, 255, 255, 0.9)",
      color: "rgb(220, 80, 80)",
      padding: "6px 10px",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      fontSize: "12px",
      zIndex: 9999,
      transition: "opacity 0.3s",
      pointerEvents: "none",
    });

    document.body.appendChild(popup);

    // Fade out after 2 seconds
    setTimeout(() => {
      popup.style.opacity = "0";
      setTimeout(() => popup.remove(), 300);
    }, 2000);
  };

  return (
   <div style={{ height: isLoading ? "600px" : "fit-content", position: "relative" }}>
      {!hasUserInteracted && (
        <div className="start-overlay" style={{ display: "none" }}>
          <button onClick={() => setHasUserInteracted(true)}>
            Click to Start
          </button>
        </div>
      )}
      {isLoading ? (
        <BlobLoader />
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : (
        <>
          <button className="fullscreen-1" id="fullscreenBtn" onClick={handleFullscreen}>
            <i className="fas fa-expand"></i>
          </button>
          <div ref={containerRef} className="svg-container" style={{ width: "100%", height: "auto" }} />
        </>
      )}
    </div>
  );
};

export function stopLayeredSVGAudio() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
  }
}

export default LayeredSVG;
